import React, { useMemo } from 'react';
import { MarketDataTable } from './MarketDataTable';
import { MarketScatterChart } from './MarketScatterChart';
import { MarketInsightCard } from './MarketInsightCard';
import { buildCrowdInsights } from '../../utils/marketAnalytics';
import {
  formatPct, formatMultiple, formatMoney, formatPlacing, formatRating, formatNumber,
  getRatingBandTone, getValueBucketTone,
} from '../../utils/marketFormatters';

function RatingPill({ row }) {
  return <span className={`mk-pill mk-pill--${getRatingBandTone(row.ratingBand)}`}>{formatRating(row.displayRating)}</span>;
}
function ValuePill({ row }) {
  return <span className={`mk-pill mk-pill--${getValueBucketTone(row.valueLabel)}`}>{row.valueLabel}</span>;
}

const WINNERS_COLUMNS = [
  { key: 'runnerName', label: 'Runner', sortable: true },
  { key: 'slateName', label: 'Showdown', sortable: true },
  { key: 'ownershipUserPct', label: 'Owned By %', align: 'right', sortable: true, render: r => formatPct(r.ownershipUserPct) },
  { key: 'finishingPosition', label: 'Finish', align: 'center', render: r => formatPlacing(r.finishingPosition) },
  { key: 'virtualEarnings', label: 'Virtual Earnings', align: 'right', sortable: true, render: r => formatMoney(r.virtualEarnings) },
  { key: 'displayRating', label: 'Rating', align: 'center', sortable: true, render: r => <RatingPill row={r} /> },
  { key: 'valueLabel', label: 'Value', align: 'center', render: r => <ValuePill row={r} /> },
];

const MISSES_COLUMNS = [
  { key: 'runnerName', label: 'Runner', sortable: true },
  { key: 'slateName', label: 'Showdown', sortable: true },
  { key: 'ownershipUserPct', label: 'Owned By %', align: 'right', sortable: true, render: r => formatPct(r.ownershipUserPct) },
  { key: 'finishingPosition', label: 'Finish', align: 'center', render: r => formatPlacing(r.finishingPosition) },
  { key: 'totalPurchaseCost', label: 'Total Spend', align: 'right', sortable: true, render: r => formatMoney(r.totalPurchaseCost) },
  { key: 'returnMultiple', label: 'Return', align: 'right', sortable: true, render: r => formatMultiple(r.returnMultiple) },
];

const GEMS_COLUMNS = [
  { key: 'runnerName', label: 'Runner', sortable: true },
  { key: 'slateName', label: 'Showdown', sortable: true },
  { key: 'displayRating', label: 'Rating', align: 'center', sortable: true, render: r => <RatingPill row={r} /> },
  { key: 'valueLabel', label: 'Value', align: 'center', render: r => <ValuePill row={r} /> },
  { key: 'ownershipUserPct', label: 'Owned By %', align: 'right', sortable: true, render: r => formatPct(r.ownershipUserPct) },
  { key: 'finishingPosition', label: 'Finish', align: 'center', render: r => formatPlacing(r.finishingPosition) },
  { key: 'returnMultiple', label: 'Return Multiple', align: 'right', sortable: true, render: r => formatMultiple(r.returnMultiple) },
];

export const MarketCrowdTab = ({ rows, loading }) => {
  const insights = useMemo(() => buildCrowdInsights(rows), [rows]);

  return (
    <div className="mk-tab-content">
      <section className="mk-section">
        <div className="mk-section-header">
          <h2 className="mk-section-title">Ownership vs Return</h2>
          <p className="mk-section-subtitle">Bubble size = total virtual dollars invested in that runner. Gold = won the race.</p>
        </div>
        <MarketScatterChart points={insights.scatterPoints} />
      </section>

      <section className="mk-section">
        <div className="mk-section-header">
          <h2 className="mk-section-title">Most-Owned Winners</h2>
          <p className="mk-section-subtitle">When the crowd got it right.</p>
        </div>
        <MarketDataTable
          columns={WINNERS_COLUMNS}
          rows={insights.mostOwnedWinners}
          getRowKey={r => `${r.slateId}-${r.raceId}-${r.runnerId}`}
          loading={loading}
          emptyIcon="🏆"
          emptyTitle="No crowd-favourite winners yet"
          emptyMessage="This table fills in as more heavily-backed runners settle."
        />
      </section>

      <section className="mk-section">
        <div className="mk-section-header">
          <h2 className="mk-section-title">Over-Owned Misses</h2>
          <p className="mk-section-subtitle">Heavily backed runners that didn't deliver.</p>
        </div>
        <MarketDataTable
          columns={MISSES_COLUMNS}
          rows={insights.overOwnedMisses}
          getRowKey={r => `${r.slateId}-${r.raceId}-${r.runnerId}`}
          loading={loading}
          emptyIcon="📉"
          emptyTitle="No over-owned misses yet"
          emptyMessage="This table fills in as more heavily-backed runners settle."
        />
      </section>

      <section className="mk-section">
        <div className="mk-section-header">
          <h2 className="mk-section-title">Hidden Gems</h2>
          <p className="mk-section-subtitle">Low-owned runners that quietly delivered a strong return.</p>
        </div>
        <MarketDataTable
          columns={GEMS_COLUMNS}
          rows={insights.hiddenGems}
          getRowKey={r => `${r.slateId}-${r.raceId}-${r.runnerId}`}
          loading={loading}
          emptyIcon="💎"
          emptyTitle="No hidden gems yet"
          emptyMessage="This table fills in as more under-owned runners settle."
        />
      </section>

      <section className="mk-section">
        <div className="mk-section-header">
          <h2 className="mk-section-title">Participation</h2>
        </div>
        <div className="mk-insight-grid">
          <MarketInsightCard
            icon="🐎" label="Avg Runners Selected Per Race" accent="blue"
            value={insights.participation.avgRunnersSelectedPerRace != null ? formatNumber(insights.participation.avgRunnersSelectedPerRace) : null}
          />
          <MarketInsightCard
            icon="💰" label="Avg Spend Per Race" accent="gold"
            value={insights.participation.avgSpendPerRace != null ? formatMoney(insights.participation.avgSpendPerRace) : null}
          />
        </div>
      </section>
    </div>
  );
};

export default MarketCrowdTab;
